/* eslint-disable react-refresh/only-export-components */
import { useAuth0 } from "@auth0/auth0-react";
import { createContext, PropsWithChildren, useContext } from "react";
import {
  AUTH0_ROLES_CLAIM,
  COSMOS_ACCESS_ROLE,
  hasCosmosAccess,
  parseAihrRoles
} from "@/lib/auth/aihr-roles";

export interface AihrUser {
  name?: string;
  email?: string;
  picture?: string;
  roles: string[];
}

interface AihrAuthContextValue {
  authEnabled: boolean;
  isLoading: boolean;
  isAuthenticated: boolean;
  error?: Error;
  user?: AihrUser;
  hasCosmosAccess: boolean;
  login: () => Promise<unknown>;
  logout: () => void;
  getAccessToken: () => Promise<string | undefined>;
}

const localUser: AihrUser = {
  name: "Local Cosmos User",
  email: "local@aihr.com",
  roles: [COSMOS_ACCESS_ROLE]
};

const localAuth: AihrAuthContextValue = {
  authEnabled: false,
  isLoading: false,
  isAuthenticated: true,
  error: undefined,
  user: localUser,
  hasCosmosAccess: true,
  login: async () => undefined,
  logout: () => undefined,
  getAccessToken: async () => undefined
};

const AihrAuthContext = createContext<AihrAuthContextValue>(localAuth);

export function LocalAuthProvider({ children }: PropsWithChildren) {
  return (
    <AihrAuthContext.Provider value={localAuth}>
      {children}
    </AihrAuthContext.Provider>
  );
}

export function Auth0Bridge({ children }: PropsWithChildren) {
  const auth0 = useAuth0();
  const roles = parseAihrRoles(auth0.user?.[AUTH0_ROLES_CLAIM]);
  const user: AihrUser | undefined = auth0.user
    ? {
        name: auth0.user.name,
        email: auth0.user.email,
        picture: auth0.user.picture,
        roles
      }
    : undefined;

  const value: AihrAuthContextValue = {
    authEnabled: true,
    isLoading: auth0.isLoading,
    isAuthenticated: auth0.isAuthenticated,
    error: auth0.error,
    user,
    hasCosmosAccess: hasCosmosAccess(roles),
    login: () =>
      auth0.loginWithRedirect({
        authorizationParams: {
          connection: "google-oauth2"
        },
        appState: {
          returnTo: window.location.pathname + window.location.search
        }
      }),
    logout: () =>
      auth0.logout({
        logoutParams: {
          returnTo: `${window.location.origin}/signin`
        }
      }),
    getAccessToken: () => auth0.getAccessTokenSilently()
  };

  return (
    <AihrAuthContext.Provider value={value}>
      {children}
    </AihrAuthContext.Provider>
  );
}

export function useAihrAuth() {
  return useContext(AihrAuthContext);
}
